import { useMutation, useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Crown,
  Loader2,
  Mail,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

/**
 * /convite/:token — pagina pública de aceite de convite multi-login.
 *
 * Flow:
 *   1. Carrega tab_convites_login_adicional pelo token
 *   2. Mostra dados (quem convidou, papel, expiração)
 *   3. Se não logado: redireciona para /cadastro com retorno
 *   4. Se logado: chama fn_aceitar_convite_login
 *   5. Sucesso → redireciona para home
 */

interface Convite {
  seq_convite_login: number;
  nom_pessoa_convidada: string;
  des_email_convidado: string;
  cod_papel: 'COTITULAR' | 'DEPENDENTE';
  cod_status: string;
  dta_expiracao: string;
  des_mensagem_personalizada: string | null;
}

const PAPEL_LABEL: Record<string, string> = {
  COTITULAR: 'Co-titular',
  DEPENDENTE: 'Dependente',
};

const PAPEL_DESCRICAO: Record<string, string> = {
  COTITULAR: 'Acesso completo à conta, igual ao titular.',
  DEPENDENTE: 'Pode gerar códigos e usar a assinatura.',
};

export function AceitarConvitePage() {
  const { token } = useParams<{ token: string }>();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();

  const convite = useQuery({
    queryKey: ['convite', token],
    queryFn: async () => {
      if (!token) throw new Error('Token ausente');
      const { data, error } = await supabase
        .from('tab_convites_login_adicional')
        .select(
          'seq_convite_login, nom_pessoa_convidada, des_email_convidado, cod_papel, cod_status, dta_expiracao, des_mensagem_personalizada',
        )
        .eq('des_token', token)
        .maybeSingle();
      if (error) throw error;
      return data as Convite | null;
    },
    enabled: !!token,
  });

  const aceitar = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('Token ausente');
      if (!user) throw new Error('Faça login primeiro');
      const { data, error } = await supabase.rpc('fn_aceitar_convite_login', {
        p_token: token,
        p_cod_auth_user: user.id,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      toast.success('Convite aceito! Você agora tem acesso a está conta.');
      setTimeout(() => navigate('/'), 1500);
    },
    onError: (e) => toast.error('Falha: ' + (e as Error).message),
  });

  const expirado = convite.data ? new Date(convite.data.dta_expiracao) < new Date() : false;
  const aceitavel =
    convite.data?.cod_status === 'ENVIADO' || convite.data?.cod_status === 'VISUALIZADO';

  const nomeMarca = tenant?.nom_tenant ?? 'Octa Pontos';

  return (
    <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Blobs de fundo — mesma estética do login/signup */}
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ duration: 1.2 }}
      />
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[22rem] w-[22rem]"
        style={{ animationDelay: '-5s' }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.15 }}
      />

      <div className="relative z-10 w-full max-w-md space-y-5">
        {/* Hero do convite */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: -8, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <div className="bg-octa-gradient shadow-octa-glow octa-float mx-auto flex h-20 w-20 items-center justify-center rounded-3xl ring-4 ring-white/40">
            <UserPlus className="h-9 w-9 text-white" />
          </div>
          <motion.div
            className="octa-glass-soft text-primary mx-auto mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Sparkles className="h-3 w-3" />
            Você foi convidado
          </motion.div>
          <motion.h1
            className="text-display-md mt-2 font-extrabold tracking-tight"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Bem-vindo ao {nomeMarca}
          </motion.h1>
          <motion.p
            className="text-muted-foreground mt-1.5 text-sm"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Aceite o convite para acessar a conta compartilhada.
          </motion.p>
        </motion.div>

        {/* Conteúdo: loading / erro / expirado / aceitável */}
        {convite.isLoading ? (
          <Card variant="glass" className="rounded-3xl border-white/40">
            <CardContent className="space-y-3 p-6">
              <Skeleton className="h-6 w-2/3 rounded-lg" />
              <Skeleton className="h-4 w-full rounded-lg" />
              <Skeleton className="h-4 w-1/2 rounded-lg" />
              <Skeleton className="h-12 w-full rounded-xl" />
            </CardContent>
          </Card>
        ) : convite.error ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card variant="glass" className="rounded-3xl border-rose-200/60">
              <CardContent className="space-y-2 p-6 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-500/15">
                  <XCircle className="text-destructive h-7 w-7" />
                </div>
                <p className="mt-2 text-sm font-semibold">Erro ao carregar convite</p>
                <p className="text-muted-foreground text-xs">{(convite.error as Error).message}</p>
              </CardContent>
            </Card>
          </motion.div>
        ) : !convite.data ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card variant="glass" className="rounded-3xl border-white/40">
              <CardContent className="space-y-2 p-6 text-center">
                <div className="bg-muted mx-auto flex h-14 w-14 items-center justify-center rounded-full">
                  <XCircle className="text-muted-foreground h-7 w-7" />
                </div>
                <p className="mt-2 text-sm font-semibold">Convite não encontrado</p>
                <p className="text-muted-foreground text-xs">
                  Verifique o link ou peça um novo convite.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ) : expirado || !aceitavel ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card variant="glass" className="rounded-3xl border-white/40">
              <CardContent className="space-y-2 p-6 text-center">
                <div className="bg-muted mx-auto flex h-14 w-14 items-center justify-center rounded-full">
                  <Clock className="text-muted-foreground h-7 w-7" />
                </div>
                <p className="mt-2 text-base font-bold">
                  {expirado
                    ? 'Convite expirado'
                    : convite.data.cod_status === 'ACEITO'
                      ? 'Convite já aceito'
                      : `Convite ${convite.data.cod_status.toLowerCase()}`}
                </p>
                <p className="text-muted-foreground text-xs">
                  Peça para o titular reenviar um novo convite.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <>
            {/* Card de dados do convite */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <Card variant="glass" className="shadow-octa-lg rounded-3xl border-white/40">
                <CardContent className="space-y-4 p-6">
                  {/* Papel — destaque visual */}
                  <div className="bg-octa-gradient/10 ring-primary/20 flex items-center gap-3 rounded-2xl p-4 ring-1">
                    <div className="bg-octa-gradient shadow-octa-glow flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl">
                      {convite.data.cod_papel === 'COTITULAR' ? (
                        <Crown className="h-6 w-6 text-white" />
                      ) : (
                        <Users className="h-6 w-6 text-white" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-primary text-[10px] font-semibold uppercase tracking-[0.16em]">
                        Seu papel
                      </p>
                      <p className="text-base font-bold tracking-tight">
                        {PAPEL_LABEL[convite.data.cod_papel]}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {PAPEL_DESCRICAO[convite.data.cod_papel]}
                      </p>
                    </div>
                  </div>

                  {/* Email convidado */}
                  <div className="border-border/60 bg-card/50 flex items-center gap-3 rounded-2xl border p-3">
                    <Mail className="text-muted-foreground h-4 w-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
                        E-mail
                      </p>
                      <p className="truncate text-sm font-semibold">
                        {convite.data.des_email_convidado}
                      </p>
                    </div>
                  </div>

                  {/* Mensagem personalizada (se existe) — glass premium em vez de amber */}
                  {convite.data.des_mensagem_personalizada && (
                    <motion.div
                      className="octa-glass-soft rounded-2xl p-4 text-sm italic"
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                    >
                      <p className="text-foreground/85">
                        &ldquo;{convite.data.des_mensagem_personalizada}&rdquo;
                      </p>
                    </motion.div>
                  )}

                  {/* Validade */}
                  <div className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
                    <Clock className="h-3 w-3" />
                    Válido até {new Date(convite.data.dta_expiracao).toLocaleString('pt-BR')}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Ações */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              {!user ? (
                <Card variant="glass" className="rounded-3xl border-white/40">
                  <CardContent className="space-y-3 p-5">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="text-primary h-4 w-4" />
                      <p className="text-sm font-semibold">
                        Faça login ou cadastre-se para aceitar.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="h-12 flex-1 rounded-xl border-2 text-base font-semibold"
                        onClick={() =>
                          navigate('/login', { state: { redirectTo: `/convite/${token}` } })
                        }
                      >
                        Entrar
                      </Button>
                      <Button
                        className="octa-cta-magic h-12 flex-1 rounded-xl text-base font-bold text-white shadow-lg"
                        onClick={() =>
                          navigate('/cadastro', {
                            state: {
                              redirectTo: `/convite/${token}`,
                              email: convite.data?.des_email_convidado,
                            },
                          })
                        }
                      >
                        Criar conta
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card variant="glass" className="rounded-3xl border-white/40">
                  <CardContent className="space-y-4 p-5">
                    <div className="flex items-center gap-3 rounded-2xl bg-emerald-50/60 p-3 ring-1 ring-emerald-200/50 dark:bg-emerald-950/30 dark:ring-emerald-900/30">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
                          Logado como
                        </p>
                        <p className="truncate text-sm font-bold">{user.email}</p>
                      </div>
                    </div>
                    <Button
                      className="octa-cta-magic octa-pulse-halo group h-14 w-full rounded-2xl text-base font-bold text-white shadow-lg transition-transform hover:scale-[1.01] active:scale-[0.98]"
                      onClick={() => aceitar.mutate()}
                      disabled={aceitar.isPending}
                    >
                      {aceitar.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Aceitando…
                        </>
                      ) : (
                        <>
                          Aceitar convite
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
